import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, memo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart,
  Buildings,
  Phone,
  WhatsappLogo,
  DotsSixVertical,
  Warning,
  ArrowSquareOut,
  Copy,
  NotePencil,
} from "@phosphor-icons/react";
import { useFavorites } from "@/store/favoritesStore";
import { HeartColorPicker } from "@/components/HeartColorPicker";
import { useViewed } from "@/store/viewedStore";
import { useLang } from "@/i18n/LanguageContext";
import { useReference } from "@/hooks/useReference";
import { usePropertyNoteText } from "@/hooks/usePropertyNoteText";
import { getAccessToken } from "@/lib/api";
import { fmtPrice, fmtDate } from "@/lib/format";
import { toWaNumber, toTelNumber } from "@/lib/phone";
import { EXTERNAL_LISTING_URL } from "@/lib/constants";
import { toast } from "sonner";
import { ColumnFilter } from "@/components/ColumnFilter";
import { ReportDialog } from "@/components/ReportDialog";
import { PropertyNoteDialog } from "@/components/PropertyNoteDialog";
import { OwnerBadge } from "@/components/OwnerBadge";
import { CallPlanBtn, CallPlanStatus } from "@/components/CallPlanControls";
import { useAuth } from "@/auth/AuthContext";
import { PhoneInfoBtn } from "@/components/PhoneInfoDialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

const STORAGE_KEY = "emlak_col_order_v3";

const isMobileDevice = () =>
  typeof window !== "undefined" && window.innerWidth < 768;

const isHouseType = (propertyType) => {
  const t = (propertyType || "").toLowerCase();
  return t.includes("fərdi") || t.includes("ferdi");
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      ok ? resolve() : reject(new Error("copy failed"));
    } catch (err) {
      reject(err);
    }
  });
};

const getAreaInfo = (property) => {
  const type = (property?.propertyType || "").toLowerCase();
  const kv = property?.generalArea ? `${property.generalArea} m²` : null;
  const sot = property?.area ? `${property.area} sot` : null;

  const isLand = type.includes("torpaq");
  const isApartment = type.includes("mənzil") || type.includes("menzil");
  const isGarage = type.includes("qaraj");
  const isHouse = type.includes("fərdi") || type.includes("ferdi");

  if (isLand) return { kv: null, sot, primary: sot };
  if (isApartment || isGarage) return { kv, sot: null, primary: kv };
  if (isHouse) return { kv, sot, primary: [kv, sot].filter(Boolean).join(", ") || null };

  return { kv, sot, primary: kv || sot || null };
};

const ALL_COLUMNS = [
  { key: "img", label: "" },
  { key: "code", label: "Kod" },
  { key: "date", label: "Tarix" },
  { key: "type", label: "Əmlak növü" },
  { key: "op", label: "Elan növü" },
  { key: "region", label: "Rayon" },
  { key: "metro", label: "Metro" },
  { key: "rooms", label: "Otaq" },
  { key: "building", label: "Tikili növü" },
  { key: "area", label: "Sahə" },
  { key: "landArea", label: "Torpaq sahəsi" },
  { key: "floor", label: "Mərtəbə" },
  { key: "price", label: "Qiymət" },
  { key: "doc", label: "Sənəd" },
  { key: "owner", label: "Satıcı" },
  { key: "person", label: "Şəxs" },
  { key: "target", label: "Nişangah" },
  { key: "contact", label: "Əlaqə" },
  { key: "plan", label: "Zəng planı" },
  { key: "fav", label: "" },
];

const DEFAULT_ORDER = ALL_COLUMNS.map((c) => c.key);

const PropertyRow = memo(function PropertyRow({
  p,
  columns,
  getColor,
  isViewed,
  colorPickerRowId,
  onToggleColorPicker,
  onSelectColor,
  onCloseColorPicker,
  onReport,
  onRowDoubleClick,
  onProtectedAction,
  onOpenNote,
  hasNotes,
  showNote,
}) {
  const heartBtnRef = useRef(null);
  const viewed = Boolean(p.isTracked) || isViewed(p.idProperty);
  const bold = viewed ? "" : "font-bold";
  const favColor = getColor(p);
  const pickerOpen = colorPickerRowId === p.idProperty;

  const { data: fetchedNote } = usePropertyNoteText(p.idProperty, showNote && hasNotes);
  const noteText = showNote && hasNotes ? fetchedNote || "" : "";

  const handlePhoneClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onProtectedAction()) return;

    const tel = toTelNumber(p.cpPhoneNumber01);
    if (!tel) return;

    window.location.href = `tel:${tel}`;
  };

  const handleCopyPhone = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onProtectedAction()) return;

    if (p.cpPhoneNumber01) {
      copyToClipboard(p.cpPhoneNumber01)
        .then(() => toast.success("Nömrə kopyalandı!"))
        .catch(() => toast.error("Nömrəni kopyalamaq mümkün olmadı."));
    }
  };

  const handleWaClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onProtectedAction()) return;

    const wa = toWaNumber(p.cpPhoneNumber01);
    if (!wa) return;

    window.open(`https://wa.me/${wa}`, "_blank", "noopener,noreferrer");
  };

  const handleGotoUrlClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!onProtectedAction()) return;

    const targetUrl = p.url || EXTERNAL_LISTING_URL;
    if (targetUrl) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  const cellMap = {
    img: (p) => (
      <td className="pl-3 py-2" key="img">
        <Link
          to={`/detail/${p.propertyCode}`}
          target={isMobileDevice() ? undefined : "_blank"}
          rel={isMobileDevice() ? undefined : "noopener noreferrer"}
          onDoubleClick={(e) => e.stopPropagation()}
          className="relative w-14 h-11 rounded-md overflow-hidden aspect-[4/3]  flex items-center justify-center"
        >
          {p.image ? (
            <img
              src={p.image}
              alt={p.propertyType || "property"}
              loading="lazy"
              className={`w-full h-full object-cover ${viewed ? "opacity-70" : ""}`}
            />
          ) : (
            <Buildings size={16} weight="duotone" className="text-muted-foreground" />
          )}
        </Link>
      </td>
    ),

    code: (p) => (
      <td className="px-3 py-2.5" key="code">
        <Link
          to={`/detail/${p.propertyCode}`}
          target={isMobileDevice() ? undefined : "_blank"}
          rel={isMobileDevice() ? undefined : "noopener noreferrer"}
          onDoubleClick={(e) => e.stopPropagation()}
          className={`font-mono hover:underline ${bold} ${viewed ? "text-muted-foreground" : "text-primary"}`}
        >
          #{p.idProperty}
        </Link>
      </td>
    ),

    date: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap text-muted-foreground ${bold}`} key="date">
        {fmtDate(p.propertyDate)}
      </td>
    ),

    type: (p) => (
      <td className="px-3 py-2.5" key="type">
        {p.propertyType && (
          <span className={`inline-flex px-2 py-0.5 rounded-md bg-accent text-accent-foreground text-xs shadow-sm ${viewed ? "font-semibold" : "font-bold"}`}>
            {p.propertyType}
          </span>
        )}
      </td>
    ),

    op: (p) => {
      const opRent = p.operationType?.toLowerCase().includes("kir") || p.operationType?.toLowerCase().includes("günl");
      return (
        <td className="px-3 py-2.5" key="op">
          {p.operationType && (
            <span className={`inline-flex px-2 py-0.5 rounded-md text-xs shadow-sm ${viewed ? "font-semibold" : "font-bold"} ${opRent ? "bg-amber-500 text-white" : "bg-emerald-600 text-white"}`}>
              {p.operationType}
            </span>
          )}
        </td>
      );
    },

    region: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap ${bold}`} key="region">
        {p.region || "—"}
      </td>
    ),

    metro: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap text-muted-foreground ${bold}`} key="metro">
        {p.metro || "—"}
      </td>
    ),

    rooms: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap ${bold}`} key="rooms">
        {p.room || "—"}
      </td>
    ),

    building: (p) => (
      <td className="px-3 py-2.5 whitespace-nowrap text-xs" key="building">
        {p.buildingType ? (
          <span className={`inline-flex px-1.5 py-0.5 rounded bg-secondary text-foreground/70 ${bold}`}>
            {p.buildingType}
          </span>
        ) : ("—")}
      </td>
    ),

    area: (p) => {
      const info = getAreaInfo(p);
      return (
        <td className={`px-3 py-2.5 whitespace-nowrap price-tabular ${bold}`} key="area">
          {info.kv || "—"}
        </td>
      );
    },

    landArea: (p) => {
      const info = getAreaInfo(p);
      return (
        <td className={`px-3 py-2.5 whitespace-nowrap price-tabular ${bold}`} key="landArea">
          {info.sot || "—"}
        </td>
      );
    },

    floor: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap price-tabular ${bold}`} key="floor">
        {p.floor ? (isHouseType(p.propertyType) ? `${p.floor}` : `${p.floor}/${p.floorOf}`) : "—"}
      </td>
    ),

    price: (p) => (
      <td className="px-3 py-2.5 whitespace-nowrap" key="price">
        <span className={`inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground text-xs px-2.5 py-1 price-tabular shadow-sm ${viewed ? "font-semibold" : "font-bold"}`}>
          {fmtPrice(p.price)} AZN
        </span>
      </td>
    ),

    doc: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap text-muted-foreground ${bold}`} key="doc">
        {p.document || "—"}
      </td>
    ),

    owner: (p) => (
      <td className="px-3 py-2.5 whitespace-nowrap" key="owner">
        {p.ownerType ? <OwnerBadge ownerType={p.ownerType} /> : <span className="text-muted-foreground">—</span>}
      </td>
    ),

    person: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap ${bold}`} key="person">
        {p.cpFullName || "—"}
      </td>
    ),

    target: (p) => (
      <td className={`px-3 py-2.5 whitespace-nowrap text-center ${bold}`} key="target">
        {p.target || "—"}
      </td>
    ),

    contact: (p) => (
      <td className="px-3 py-2.5 whitespace-nowrap" key="contact">
        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={handlePhoneClick}
            data-testid={`row-phone-${p.idProperty}`}
            className="w-7 h-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-sm hover:brightness-110 transition"
            aria-label="Zəng et"
            title="Nömrəni göstər"
          >
            <Phone size={12} weight="fill" />
          </button>

          <button
            type="button"
            onClick={handleWaClick}
            data-testid={`row-whatsapp-${p.idProperty}`}
            className="w-7 h-7 rounded-full btn-whatsapp flex items-center justify-center shadow-sm transition"
            aria-label="WhatsApp"
            title="WhatsApp"
          >
            <WhatsappLogo size={12} weight="fill" />
          </button>

          <button
            type="button"
            onClick={handleCopyPhone}
            data-testid={`row-copy-${p.idProperty}`}
            className="w-7 h-7 rounded-full bg-[#3B82F6] text-white flex items-center justify-center shadow-sm hover:bg-[#2563EB] transition"
            aria-label="Nömrəni kopyala"
            title="Nömrəni kopyala"
          >
            <Copy size={12} weight="bold" />
          </button>
        

          {getAccessToken() && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenNote(p.idProperty);
              }}
              data-testid={`row-note-${p.idProperty}`}
              className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shadow-sm transition ${hasNotes
                ? "bg-[#EA580C] border-[#EA580C] text-white hover:bg-[#C2410C] hover:border-[#C2410C]"
                : "bg-white border-[#EA580C] text-[#EA580C] hover:bg-orange-50"
                }`}
              aria-label="Qeyd"
              title={hasNotes ? "Qeyd var — dəyiş" : "Qeyd əlavə et"}
            >
              <NotePencil size={12} weight={hasNotes ? "fill" : "bold"} />
            </button>
          )}
        </div>
      </td>
    ),

    plan: (p) => (
      <td className="px-3 py-2.5 whitespace-nowrap" key="plan">
        <CallPlanStatus property={p} compact />
      </td>
    ),

    fav: (p) => (
      <td className="px-3 py-2.5" key="fav">
        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
          <CallPlanBtn
            property={p}
            size="w-8 h-8"
            className="border border-border/60 !shadow-none"
          />

          <div className="relative">
            <button
              ref={heartBtnRef}
              onClick={(e) => onToggleColorPicker(e, p)}
              data-testid={`row-fav-${p.idProperty}`}
              className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors"
              aria-label="favorite"
            >
              <Heart
                size={14}
                weight={favColor ? "fill" : "regular"}
                style={favColor ? { color: favColor } : undefined}
                className={!favColor ? "text-muted-foreground" : ""}
              />
            </button>
            {pickerOpen && (
              <HeartColorPicker
                activeColor={favColor}
                onSelect={(color) => onSelectColor(p, color)}
                onClose={onCloseColorPicker}
                anchorRef={heartBtnRef}
              />
            )}
          </div>

          <button
            onClick={(e) => onReport(e, p.idProperty)}
            data-testid={`row-report-${p.idProperty}`}
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center hover:border-destructive hover:bg-destructive/5 text-muted-foreground hover:text-destructive transition-colors"
            aria-label="report"
            title="Şikayət et"
          >
            <Warning size={13} weight="bold" />
          </button>

          <button
            type="button"
            onClick={handleGotoUrlClick}
            data-testid={`row-goto-url-${p.idProperty}`}
            className="w-8 h-8 rounded-full border border-border/60 flex items-center justify-center hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary transition-colors"
            aria-label="Mənbə saytda aç"
            title="Mənbə saytda aç"
          >
            <ArrowSquareOut size={13} weight="bold" />
          </button>
        </div>
      </td>
    ),
  };

  return (
    <>
      <tr
        data-testid={`property-row-${p.idProperty}`}
        onDoubleClick={() => onRowDoubleClick(p)}
        className={`${noteText ? "" : "border-b border-border/40"} hover:bg-primary/[0.03] transition-colors row-accent cursor-pointer ${viewed ? "bg-muted/30" : ""}`}
      >
        {columns.map((c) => cellMap[c.key] ? cellMap[c.key](p) : null)}
      </tr>

      {noteText && (
        <tr
          data-testid={`property-note-row-${p.idProperty}`}
          className={`border-b border-border/40 ${viewed ? "bg-muted/30" : ""}`}
        >
          <td colSpan={columns.length} className="px-3 pb-2.5 pt-0">
            <div className="sticky left-3 flex w-full items-center gap-1.5 rounded-lg border border-[#EA580C]/30 bg-[#EA580C]/10 px-2.5 py-1.5 text-[11px] leading-snug text-foreground">
              <NotePencil size={12} weight="fill" className="shrink-0 text-[#EA580C]" />
              <p className="min-w-0 flex-1 truncate" title={noteText}>
                {noteText}
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

export const PropertyTable = ({
  items = [],
  filters,
  setFilters,
  showNote = false,
  showCallPlan = false,
}) => {
  const { t } = useLang();
  const { ref } = useReference();
  const nav = useNavigate();
  const auth = useAuth();

  const isPackageActive = Boolean(auth?.isPackageActive || auth?.hasActivePackage);

  const getColorFn = useFavorites((s) => s.getColor);
  const setColorFn = useFavorites((s) => s.setColor);
  const isViewedFn = useViewed((s) => s.isViewed);

  const getColorSafe = useCallback(
    (p) => (getAccessToken() ? getColorFn(p) : null),
    [getColorFn]
  );

  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (
        Array.isArray(saved) &&
        saved.length === DEFAULT_ORDER.length &&
        saved.every((k) => DEFAULT_ORDER.includes(k))
      ) {
        return saved;
      }
    } catch { }
    return DEFAULT_ORDER;
  });

  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [noteId, setNoteId] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [colorPickerRowId, setColorPickerRowId] = useState(null);
  const [authModalType, setAuthModalType] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  }, [order]);

  const columns = useMemo(
    () =>
      order
        .map((k) => ALL_COLUMNS.find((c) => c.key === k))
        .filter(Boolean)
        .filter((c) => c.key !== "plan" || showCallPlan),
    [order, showCallPlan]
  );

  const handleProtectedAction = useCallback(() => {
    const token = getAccessToken();
    if (!token) {
      setAuthModalType("login");
      return false;
    }

    if (!isPackageActive) {
      setAuthModalType("package");
      return false;
    }

    return true;
  }, [isPackageActive]);

  const onDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", key);
    } catch { }
  };

  const onDragOver = (key) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overKey !== key) setOverKey(key);
  };

  const onDrop = (key) => (e) => {
    e.preventDefault();
    const src = dragKey || e.dataTransfer.getData("text/plain");

    if (!src || src === key) {
      setDragKey(null);
      setOverKey(null);
      return;
    }

    setOrder((prev) => {
      const next = [...prev];
      const srcIdx = next.indexOf(src);
      const dstIdx = next.indexOf(key);
      if (srcIdx < 0 || dstIdx < 0) return prev;

      next.splice(srcIdx, 1);
      next.splice(dstIdx, 0, src);
      return next;
    });

    setDragKey(null);
    setOverKey(null);
  };

  const onDragEnd = () => {
    setDragKey(null);
    setOverKey(null);
  };

  const onToggleColorPicker = useCallback((e, property) => {
    e.preventDefault();
    e.stopPropagation();

    if (!getAccessToken()) {
      setAuthModalType("login");
      return;
    }

    setColorPickerRowId((prev) => (prev === property.idProperty ? null : property.idProperty));
  }, []);

  const onSelectColor = useCallback(
    (property, color) => {
      setColorFn(property, color);
      toast(color ? t("common.addedToFavorites") : t("common.removedFromFavorites"));
    },
    [setColorFn, t]
  );

  const onCloseColorPicker = useCallback(() => {
    setColorPickerRowId(null);
  }, []);

  const onReport = useCallback((e, idProperty) => {
    e.preventDefault();
    e.stopPropagation();

    if (!getAccessToken()) {
      setAuthModalType("login");
      return;
    }

    setReportId(idProperty);
  }, []);

  const onOpenNote = useCallback((idProperty) => {
    if (!getAccessToken()) {
      setAuthModalType("login");
      return;
    }

    setNoteId(idProperty);
  }, []);

  const onRowDoubleClick = useCallback((property) => {
    const detailUrl = `/detail/${property.propertyCode}`;

    if (isMobileDevice()) {
      nav(detailUrl);
      return;
    }

    window.open(detailUrl, "_blank", "noopener,noreferrer");
  }, [nav]);

  const handleModalConfirm = () => {
    const target =
      authModalType === "login"
        ? "/login"
        : "/payments?autoOpen=true";
    setAuthModalType(null);
    nav(target);
  };

  const hasFilters = !!(filters && setFilters);

  const F = (props) =>
    hasFilters ? <ColumnFilter filters={filters} setFilters={setFilters} {...props} /> : null;

  const filterMap = {
    code: () => <F type="text" keys={["code"]} label="Kod" />,
    type: () => <F type="select" keys={["idPropertyType"]} label="Əmlak növü" options={ref?.propertyTypes} />,
    op: () => <F type="select" keys={["idOperationType"]} label="Elan növü" options={ref?.operationTypes} />,
    region: () => <F type="select" keys={["regionIds"]} label="Rayon" options={ref?.regions} />,
    metro: () => <F type="select" keys={["metroIds"]} label="Metro" options={ref?.metros} />,
    rooms: () => <F type="select" keys={["roomIds"]} label="Otaq" options={ref?.roomCounts} />,
    building: () => <F type="select" keys={["buildingTypeIds"]} label="Tikili növü" options={ref?.buildingTypes} />,
    area: () => <F type="range" keys={["areaMin", "areaMax"]} label="Sahə (m²)" />,
    floor: () => <F type="range" keys={["floorMin", "floorMax"]} label="Mərtəbə" />,
    price: () => <F type="range" keys={["priceMin", "priceMax"]} label="Qiymət (AZN)" />,
    doc: () => <F type="select" keys={["idDocument"]} label="Sənəd" options={ref?.documents} />,
    owner: () => <F type="select" keys={["idOwnerType"]} label="Satıcı tipi" options={ref?.ownerTypes} />,
  };

  const scrollRef = useRef(null);
  const barRef = useRef(null);
  const syncingFrom = useRef(null);

  const [scrollWidth, setScrollWidth] = useState(0);
  const [hasOverflow, setHasOverflow] = useState(false);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const measure = () => {
      setScrollWidth(el.scrollWidth);
      setHasOverflow(el.scrollWidth > el.clientWidth + 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [columns, items.length]);

  const onTableScroll = () => {
    if (syncingFrom.current === "bar") {
      syncingFrom.current = null;
      return;
    }
    if (!barRef.current || !scrollRef.current) return;
    syncingFrom.current = "table";
    barRef.current.scrollLeft = scrollRef.current.scrollLeft;
  };

  const onBarScroll = () => {
    if (syncingFrom.current === "table") {
      syncingFrom.current = null;
      return;
    }
    if (!barRef.current || !scrollRef.current) return;
    syncingFrom.current = "bar";
    scrollRef.current.scrollLeft = barRef.current.scrollLeft;
  };

  return (
    <div data-testid="property-table">
      <div className={`border border-border/60 bg-card shadow-sm overflow-hidden ${hasOverflow ? "rounded-t-xl" : "rounded-xl"}`}>
        <div className="overflow-x-auto scrollbar-hidden" ref={scrollRef} onScroll={onTableScroll}>
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-b from-secondary/80 to-secondary/40 text-left">
              <tr>
                {columns.map((c) => {
                  const isDragging = dragKey === c.key;
                  const isOver = overKey === c.key && dragKey && dragKey !== c.key;

                  return (
                    <th
                      key={c.key}
                      data-testid={`col-header-${c.key}`}
                      draggable
                      onDragStart={onDragStart(c.key)}
                      onDragOver={onDragOver(c.key)}
                      onDrop={onDrop(c.key)}
                      onDragEnd={onDragEnd}
                      className={`px-3 py-3 whitespace-nowrap border-b border-border/60 font-semibold cursor-move select-none transition-all ${isDragging ? "opacity-40" : ""
                        } ${isOver ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}
                      title="Sürüyərək sırasını dəyişin"
                    >
                      <div className={`inline-flex items-center gap-1 ${c.key === "target" ? "justify-center w-full" : ""}`}>
                        {c.label && (
                          <DotsSixVertical size={12} weight="bold" className="text-muted-foreground/50 opacity-0 group-hover:opacity-100" />
                        )}
                        <span className="label-tiny">{c.label}</span>
                        {filterMap[c.key] && filterMap[c.key]()}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {items.map((p) => (
                <PropertyRow
                  key={p.idProperty}
                  p={p}
                  columns={columns}
                  getColor={getColorSafe}
                  isViewed={isViewedFn}
                  colorPickerRowId={colorPickerRowId}
                  onToggleColorPicker={onToggleColorPicker}
                  onSelectColor={onSelectColor}
                  onCloseColorPicker={onCloseColorPicker}
                  onReport={onReport}
                  onRowDoubleClick={onRowDoubleClick}
                  onProtectedAction={handleProtectedAction}
                  onOpenNote={onOpenNote}
                  hasNotes={notesMap[p.idProperty] ?? Boolean(p.hasNotes)}
                  showNote={showNote}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasOverflow && (
        <div
          className="sticky bottom-0 z-10 overflow-x-auto rounded-b-xl border border-t-0 border-border/60 bg-card shadow-sm"
          style={{ height: 14 }}
          ref={barRef}
          onScroll={onBarScroll}
          data-testid="property-table-scrollbar"
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
      )}

      <ReportDialog
        propertyId={reportId}
        open={!!reportId}
        onOpenChange={(v) => !v && setReportId(null)}
      />

      <PropertyNoteDialog
        propertyId={noteId}
        open={!!noteId}
        onOpenChange={(v) => !v && setNoteId(null)}
        onNoteChange={(v) => setNotesMap((prev) => ({ ...prev, [noteId]: v }))}
      />

      <AlertDialog
        open={!!authModalType}
        onOpenChange={(v) => !v && setAuthModalType(null)}
      >
        <AlertDialogContent data-testid="auth-required-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {authModalType === "login" ? "Giriş tələb olunur" : "Aktiv Paketiniz Yoxdur"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {authModalType === "login"
                ? "Sistemdən daha rahat istifadə üçün şəxsi kabinetidən istifadə tələb edilir."
                : "Nömrəni görmək, kopyalamaq, WhatsApp vasitəsilə əlaqə saxlamaq və ya mənbə keçidinə daxil olmaq üçün zəhmət olmasa ödəniş edərək paketinizi yeniləyin"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="auth-modal-cancel">Xeyr</AlertDialogCancel>
            <AlertDialogAction data-testid="auth-modal-confirm" onClick={handleModalConfirm}>
              {authModalType === "login" ? "Davam et" : "Paketlərə keç"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};